// src/components/course/SentenceCard.js
import React from 'react';
import clsx from 'clsx'; // Using clsx for cleaner class management
import { useTranslation } from '../../hooks/useTranslation';
import { srsMapping } from '../../utils/mappings';
import GlassyBadge from '../common/GlassyBadge';

const SentenceCard = ({ sentence, onCardClick, learnedIds }) => {
  const [showTranslation] = useTranslation();

  // --- Card Data and Logic ---
  // Prefer live learnedIds set (from MetadataProvider) over stored item field to avoid stale state
  const isLearned = learnedIds ? learnedIds.has(sentence.id) : false;
  const isSkipped = sentence.skipped || false;
  const srsStage = isLearned ? (isSkipped ? 7 : sentence.srs_stage ?? 0) : 0;
  const nLevel = sentence.level ?? 0;

  // --- Dynamic styles ---
  // MODIFIED: This function now only handles styles that CANNOT be in the CSS component layer
  const getDynamicStyles = () => {
    if (isLearned) {
      return {
        borderTopColor: `var(--color-srs-${srsStage})`,
        boxShadow: `0 4px 16px var(--color-srs-${srsStage})30`,
      };
    }
    return {
      borderTopColor: 'var(--color-border)', // border color
    };
  };

  const cardStyles = getDynamicStyles();

  // --- Card Classes ---
  // MODIFIED: Replaced hardcoded hover effects with your CSS component classes
  const cardClasses = clsx(
    'group',
    'relative',
    'flex',
    'flex-col',
    'justify-between',
    'p-4',
    'rounded-xl',
    'backdrop-blur-sm',
    'border-2',
    'border-border',
    'border-t-4',
    'overflow-hidden',
    'cursor-pointer',
    'hover:shadow-lg', // Keep card-specific shadow
    {
      'item-unlearned item-unlearned-hover': !isLearned,
      'item-learned item-learned-hover': isLearned,
    }
  );

  return (
    <div onClick={onCardClick} className={cardClasses} style={cardStyles}>
      {/* Glassmorphic overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />

      {/* --- Main Content --- */}
      <div className="relative z-10 flex flex-col items-center justify-center flex-grow w-full py-2 overflow-hidden">
        <p
          className="text-xl font-medium leading-snug my-auto truncate w-full text-center drop-shadow-sm text-content-primary transition-colors duration-75 group-hover:text-primary"
          lang="ja"
        >
          {sentence.sentence}
        </p>

        {/* Translation with sliding animation */}
        <div className={`overflow-hidden transition-all duration-300 ease-in-out w-full ${showTranslation ? 'max-h-8 mt-2' : 'max-h-0 mt-0'}`}>
          <p className="text-sm truncate w-full text-center drop-shadow-sm text-content-secondary opacity-80 transition-colors duration-75 group-hover:text-content-primary group-hover:opacity-100">
            {sentence.translation}
          </p>
        </div>
      </div>

      {/* --- FOOTER --- */}
      <div className="relative z-10 flex justify-center items-center pt-3 border-t border-border min-h-[34px] gap-2">
        <GlassyBadge level={nLevel} size="md" variant="solid">
          N{nLevel}
        </GlassyBadge>
        {sentence.kanji_complexity != null && (
          <GlassyBadge color="blue" size="md" variant="solid">
            {sentence.kanji_complexity === 61 ? '漢61+' : `漢${sentence.kanji_complexity}`}
          </GlassyBadge>
        )}
        {isLearned && (
          <GlassyBadge srsStage={srsStage} size="md" variant="solid">
            {srsMapping[srsStage]}
          </GlassyBadge>
        )}
      </div>
    </div>
  );
};

export default SentenceCard;
